import { computed, nextTick, ref, toRef } from 'vue';
import { uniq } from 'lodash';
import { useI18n } from '@/composables';
import { useFlowItemFieldErrors } from '@/composables/useFlowDialogFieldErrors';
import { $emitter } from '@/services/emitter';
import { useStore } from '@/store';
import { useCampaignFlowSelectOptions } from './useCampaignFlowSelectOptions';

export type FlowLandingConfirm = {
  item: { id: string; offers?: number };
  oldItem: { id: string; weight: number; offers?: number };
};

type CampaignFlowLandingProps = {
  path: {
    landings: Array<{ id: string; weight: number; offers?: number; url?: string }>;
    offers: Array<{ id: string; weight: number }>;
  };
  ruleIndex: number;
  pathIndex: number;
  landingIndex: number;
  landing: { id: string; weight: number; offers?: number; url?: string };
  directLinking: boolean;
  isDisabledWeight: boolean;
  totalWeight: number;
  errorConditionPrefix?: string[];
  errorsRaw: unknown[][] | null;
  landingOptions: Landing[];
  form: {
    workspaceId: string;
    countryId?: string;
  };
};

export const useCampaignFlowLanding = (
  props: CampaignFlowLandingProps,
  emit: {
    (event: 'delete'): void;
    (event: 'edit'): void;
    (event: 'preview', url: string): void;
    (event: 'fix-landing-count', item: { offers: number }, preserveLandings?: boolean): void;
  },
) => {
  const { t } = useI18n();
  const store = useStore();
  const { fetchLandingOptions } = useCampaignFlowSelectOptions(() => props.form);
  const oldValue = ref<{ id: string; weight: number; offers?: number } | null>(null);

  const landingIds = computed(() =>
    props.path.landings.map((item) => item.id).filter(Boolean),
  );

  const currentOffersCount = computed(() => props.path.offers.length);

  const errorCondition = computed(() => [
    ...(props.errorConditionPrefix ?? []),
    'rules',
    props.ruleIndex + 1,
    'paths',
    props.pathIndex + 1,
    'landings',
    props.landingIndex + 1,
  ]);

  const { generalErrorMessages, weightErrorMessages } = useFlowItemFieldErrors({
    errorsRaw: toRef(props, 'errorsRaw'),
    errorCondition,
  });

  const getItem = (id: string) => props.path.landings.find((item) => item.id === id);

  const landingUrl = computed(() => getItem(props.landing.id)?.url || '');

  const landingPercentage = computed(() => {
    if (props.totalWeight === 0) {
      return 0;
    }

    return Math.round((props.landing.weight / props.totalWeight) * 100);
  });

  const handleConfirm = (payload: FlowLandingConfirm) => {
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: {
        onConfirm: () => {
          const { item } = payload;
          emit('fix-landing-count', item);
        },
      },
    });
  };

  const handleCancel = (payload: FlowLandingConfirm) => {
    const { oldItem } = payload;

    nextTick(() => {
      props.path.landings.splice(props.landingIndex, 1, oldItem);
    });
  };

  const onChange = (value: { id: string; offers?: number } | null) => {
    oldValue.value = props.landing;

    props.path.landings.splice(props.landingIndex, 1, {
      weight: props.landing.weight,
      ...value,
    } as { id: string; weight: number; offers?: number });

    if (!value) {
      return false;
    }

    if (value.offers === 1) {
      return true;
    }

    const landings = store.cache.landings.data
      .filter((item) => landingIds.value.includes(item.id))
      .map((item) => item.offers)
      .concat(value.offers)
      .filter((item: number) => item > 1);

    const uniqLandingsOffersCount = uniq(landings);

    const currentLandingOffersCount = uniqLandingsOffersCount[0] || 1;
    const offersCanBeDeleted = currentOffersCount.value > 1 && currentLandingOffersCount > value.offers;
    const landingsCanBeDeleted =
      props.path.landings.length > 1 && uniqLandingsOffersCount.length > 1;

    if (offersCanBeDeleted || landingsCanBeDeleted) {
      const payload: FlowLandingConfirm = {
        item: value,
        oldItem: oldValue.value || props.landing,
      };

      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: {
          message: t('You are adding lander with different number of offers. Are you sure?'),
          onConfirm: () => handleConfirm(payload),
          onCancel: () => handleCancel(payload),
        },
      });
    } else if (value.offers > 1) {
      emit('fix-landing-count', value, true);
    }
  };

  return {
    fetchLandingOptions,
    generalErrorMessages,
    weightErrorMessages,
    landingUrl,
    landingPercentage,
    onChange,
  };
};
